import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useLazyQuery } from '@apollo/client'
import {
  ActionIcon,
  Divider,
  Loader,
  NumberInput,
  Select,
  Text,
} from '@mantine/core'
import dayjs from 'dayjs'
import utcPlugin from 'dayjs/plugin/utc'
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react'
import type { LinkType } from 'types/graphql'

import { useMutation, useQuery } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import DNButton from 'src/components/Buttons/Button/DNButton'
import DnDrawer from 'src/components/Core/DnDrawer'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  GET_MARKETING_BUDGET,
  UPSERT_MARKETING_BUDGET,
} from 'src/graphql/marketingBudgets.graphql'
import { GET_PIPELINE_SOURCE_BREAKDOWN } from 'src/graphql/pipelineMetrics.graphql'
import { LinkTypeDisplay } from 'src/graphql/types/trackingLinks'
import { useCompanyLeadSourceOptions } from 'src/hooks/useCompanyLeadSourceOptions/useCompanyLeadSourceOptions'
import { canAccessMarketingCosts } from 'src/lib/auth/permissions'
import { LEAD_SOURCE_NO_NAME } from 'src/lib/leadSource.utils'

dayjs.extend(utcPlugin)

/**
 * Whole dollars normally, cents only when a figure actually has them. Budgets
 * are keyed in as round numbers, so cents everywhere would be noise — but
 * rounding everything hides real money: two lines of $1,000.50 and $999.60
 * render as $1,001 and $1,000 above a header total of $2,000, which reads as
 * the total being wrong by a dollar. Formatting the total and its lines by the
 * same rule keeps them reconcilable.
 */
const money = {
  format: (value: number): string =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
      maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
    }).format(value),
}

/** Width of the value column, matching the Form*Field rows used app-wide. */
const VALUE_WIDTH = 'w-[250px]'

export interface BudgetLine {
  marketingBudgetLineId?: string
  linkType?: LinkType | null
  linkName?: string | null
  amount: number
}

export interface BudgetData {
  marketingBudgetId?: string
  month: string
  effectiveTotal?: number | null
  lines: BudgetLine[]
}

/**
 * Exactly the fields MarketingBudgetLineInput accepts. Kept separate from
 * `BudgetLine` (which carries the server's `marketingBudgetLineId` and
 * Apollo's `__typename`) because GraphQL rejects any unknown field on an
 * input object — spreading a fetched line into the mutation fails every save.
 */
export interface BudgetLineInput {
  linkType?: LinkType | null
  linkName?: string | null
  amount: number
}

export interface UpsertBudgetInput {
  month: string
  lines: BudgetLineInput[]
}

/** Local edit row — `key` keeps React identity stable while rows are edited. */
interface DraftLine extends BudgetLineInput {
  key: string
}

let draftKeySeq = 0
const nextKey = () => `draft-${(draftKeySeq += 1)}`

const toDraft = (lines: (BudgetLine | BudgetLineInput)[]): DraftLine[] =>
  lines.map((line) => ({
    linkType: line.linkType ?? null,
    linkName: line.linkName ?? null,
    amount: Number(line.amount) || 0,
    key: nextKey(),
  }))

/**
 * Groups lines for the read-only view: each source with its link-name
 * sub-lines beneath it. Mirrors how the Pipeline by Source table nests its
 * drill-down, so the two read the same way.
 */
const groupForDisplay = (lines: BudgetLine[]) => {
  const sources = new Map<string, { total: number; children: BudgetLine[] }>()

  for (const line of lines) {
    if (!line.linkType) continue
    const entry = sources.get(line.linkType) ?? { total: 0, children: [] }
    entry.total += Number(line.amount)
    if (line.linkName) entry.children.push(line)
    sources.set(line.linkType, entry)
  }

  return [...sources.entries()]
}

const sourceLabel = (linkType: string) => LinkTypeDisplay[linkType] ?? linkType

/**
 * Label-left / value-right row, matching the Form*Field convention used by the
 * Employment and Job Listing drawers: the same row shape in read and edit
 * mode, with the control swapped in on the right.
 */
const DetailRow = ({
  label,
  indent = false,
  children,
  testId,
}: {
  label: React.ReactNode
  indent?: boolean
  children: React.ReactNode
  testId?: string
}) => (
  <div
    className="flex flex-row items-center justify-between gap-2"
    data-testid={testId}
  >
    <Text size="xs" fw={300} className={indent ? 'pl-4' : undefined}>
      {label}
    </Text>
    <div className={`flex ${VALUE_WIDTH} items-center justify-end gap-1`}>
      {children}
    </div>
  </div>
)

export interface MarketingBudgetDrawerViewProps {
  month: string
  budget?: BudgetData | null
  loading?: boolean
  canEdit: boolean
  saving?: boolean
  onMonthChange: (month: string) => void
  onSave: (input: UpsertBudgetInput) => void
  /**
   * Fetches the previous month's lines. Resolving with lines loads them into
   * the editor as unsaved edits; resolving with null leaves it untouched (the
   * container surfaces "nothing to copy").
   */
  onCopyPrevious?: () => Promise<BudgetLineInput[] | null>
  copyPreviousLabel?: string
  /**
   * Incremented by the container after each successful save; returns the view
   * to read-only so the saved budget is what's on screen. Paired with
   * `savedMonth` so a slow save for one month cannot close an editor the user
   * has since opened on another.
   */
  savedAt?: number
  savedMonth?: string | null
  /** Source options for the line editor; falls back to a free list. */
  sourceOptions?: { value: string; label: string }[]
  nameOptionsBySource?: Record<string, string[]>
  /**
   * Set when the month's budget could not be fetched. Distinct from "no budget
   * for this month": both leave `budget` null, but only one of them means the
   * month is genuinely empty. Saving from a mistaken empty state would delete
   * the lines that failed to load, since the upsert rewrites a whole month.
   */
  loadFailed?: boolean
  onRetryLoad?: () => void
}

export const MarketingBudgetDrawerView = ({
  month,
  budget,
  loading = false,
  canEdit,
  saving = false,
  onMonthChange,
  onSave,
  onCopyPrevious,
  copyPreviousLabel,
  loadFailed = false,
  onRetryLoad,
  savedAt = 0,
  savedMonth = null,
  sourceOptions = [],
  nameOptionsBySource = {},
}: MarketingBudgetDrawerViewProps) => {
  const [editing, setEditing] = useState(false)
  const [draftLines, setDraftLines] = useState<DraftLine[]>([])
  const [error, setError] = useState<string | null>(null)

  const monthLabel = dayjs.utc(month).format('MMMM YYYY')
  // Memoized so the `??` fallback doesn't hand startEditing a new array
  // identity on every render.
  const lines = useMemo(() => budget?.lines ?? [], [budget])
  const effectiveTotal = budget?.effectiveTotal ?? null

  // Changing month always drops out of edit mode, so unsaved edits can't
  // silently follow the user onto a different month's budget.
  useEffect(() => {
    setEditing(false)
    setError(null)
  }, [month])

  // A completed save exits edit mode so the drawer shows what was persisted —
  // but only for the month that was actually saved. Without the month check, a
  // slow save for July closes an editor the user has since opened on August and
  // discards every line they typed there, with no warning and no save offered.
  useEffect(() => {
    if (savedAt === 0) return
    if (savedMonth != null && savedMonth !== month) return
    setEditing(false)
    setError(null)
    // `month` is deliberately absent: this must run when a save lands, not
    // when the user navigates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedAt])

  const startEditing = useCallback(() => {
    setDraftLines(toDraft(lines))
    setError(null)
    setEditing(true)
  }, [lines])

  const draftTotal = useMemo(
    () => draftLines.reduce((sum, line) => sum + (Number(line.amount) || 0), 0),
    [draftLines]
  )

  // Copied lines land as unsaved edits in the open editor — the draft state is
  // seeded once on entering edit mode, so the fetch has to write into it here
  // rather than relying on a new `budget` prop arriving.
  const handleCopyPrevious = useCallback(async () => {
    const copied = await onCopyPrevious?.()
    if (!copied?.length) return
    setDraftLines(toDraft(copied))
    setError(null)
  }, [onCopyPrevious])

  const updateLine = (key: string, patch: Partial<DraftLine>) =>
    setDraftLines((prev) =>
      prev.map((line) => (line.key === key ? { ...line, ...patch } : line))
    )

  const removeLine = (key: string) =>
    setDraftLines((prev) => prev.filter((line) => line.key !== key))

  const addSourceLine = () =>
    setDraftLines((prev) => [
      ...prev,
      { key: nextKey(), linkType: null, linkName: null, amount: 0 },
    ])

  /** Inserts the sub-line directly beneath its source so the list stays read-able. */
  const addNameLine = (afterKey: string, linkType: LinkType) =>
    setDraftLines((prev) => {
      const index = prev.findIndex((line) => line.key === afterKey)
      const next = [...prev]
      next.splice(index + 1, 0, {
        key: nextKey(),
        linkType,
        linkName: '',
        amount: 0,
      })
      return next
    })

  /** Mirrors the server-side rules so Save can't fail on something visible. */
  const validate = (): string | null => {
    if (draftLines.length === 0) return 'Add at least one budget line.'
    const seen = new Set<string>()
    for (const line of draftLines) {
      if (Number(line.amount) < 0) return 'Budget amounts must be zero or more.'
      // Every line has to name a source we support — there is no free-text
      // budget line.
      if (!line.linkType) {
        return 'Pick a source for every budget line, or remove it.'
      }
      if (line.linkName != null && line.linkName.trim() === '') {
        return 'Pick a link name, or remove the sub-line.'
      }
      const key = `${line.linkType}:${line.linkName ?? ''}`
      if (seen.has(key)) return 'Two lines target the same source.'
      seen.add(key)
    }
    return null
  }

  const handleSave = () => {
    const validationError = validate()
    if (validationError) {
      setError(validationError)
      return
    }
    onSave({
      month,
      // Whitelist — never spread a draft line. Lines loaded from the server
      // carry `marketingBudgetLineId` and Apollo's `__typename`, and GraphQL
      // rejects unknown fields on an input object, so a spread would fail
      // every save of an existing budget.
      lines: draftLines.map((line) => ({
        linkType: line.linkType ?? null,
        linkName: line.linkName ?? null,
        amount: Number(line.amount) || 0,
      })),
    })
  }

  const stepMonth = (delta: 1 | -1) =>
    onMonthChange(
      dayjs.utc(month).add(delta, 'month').startOf('month').toISOString()
    )

  const grouped = groupForDisplay(lines)
  const total = editing ? draftTotal : effectiveTotal

  return (
    <div className="flex h-screen flex-col space-y-4 pb-14">
      {/* HEADER — no close control: clicking outside the drawer dismisses it,
          which is how every other detail drawer in the app behaves. */}
      <div className="flex flex-row items-center justify-between">
        <Text size="lg" fw={500}>
          Marketing Budget
        </Text>
        <div className="flex items-center gap-2">
          {!editing && canEdit && !loading && !loadFailed && (
            <DNButton
              variant="transparent"
              onClick={startEditing}
              data-testid="edit-budget-button"
            >
              <Pencil size={12} />
              {lines.length === 0 ? 'Add' : 'Edit'}
            </DNButton>
          )}
          {editing && !saving && (
            <DNButton
              variant="transparent"
              className="text-doubleNickel-success-500"
              onClick={handleSave}
              data-testid="save-budget-button"
            >
              <Check size={14} />
              Save
            </DNButton>
          )}
          {(loading || saving) && <Loader size={12} color="gray" />}
        </div>
      </div>
      <Divider variant="dashed" />

      {/* BODY */}
      <div className="flex h-screen flex-col space-y-4 overflow-auto">
        {/* Centered so the chevrons read as a pair either side of the month. */}
        <div className="flex items-center justify-center gap-2">
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            aria-label="Previous month"
            onClick={() => stepMonth(-1)}
          >
            <ChevronLeft size={16} />
          </ActionIcon>
          <Text size="sm" fw={500} data-testid="drawer-month">
            {monthLabel}
          </Text>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            aria-label="Next month"
            onClick={() => stepMonth(1)}
          >
            <ChevronRight size={16} />
          </ActionIcon>
        </div>

        {/* The month's total leads the drawer. Tinted like the featured hero
            stat on this page (brand-100 border over a blue wash). */}
        <div className="flex flex-col gap-0.5 rounded-lg border border-doubleNickel-brand-100 bg-doubleNickel-brand-25 px-4 py-3">
          <Text
            size="xs"
            fw={600}
            className="uppercase tracking-wide text-doubleNickel-brand-700"
          >
            {monthLabel} total
          </Text>
          <Text
            size="xl"
            fw={700}
            className="text-doubleNickel-gray-900"
            data-testid="drawer-total"
          >
            {total != null ? money.format(total) : '—'}
          </Text>
        </div>

        <Text size="xs" fw={400} className="text-doubleNickel-gray-500">
          BUDGET BY SOURCE
        </Text>

        {loading ? (
          <Text size="xs" fw={400} className="text-doubleNickel-gray-500">
            Loading budget…
          </Text>
        ) : editing ? (
          <>
            <div className="space-y-3">
              {draftLines.map((line) => {
                const isNameLine = line.linkName != null
                return (
                  <div
                    key={line.key}
                    className="flex flex-row items-center gap-2"
                    data-testid="budget-line-row"
                  >
                    {/* Name column: a plain label once the choice is made, a
                        Select while it is still open — same rule for a source
                        and for a link name, so rows read consistently. */}
                    <div
                      className={`min-w-0 flex-1 ${isNameLine ? 'pl-4' : ''}`}
                    >
                      {isNameLine ? (
                        <Select
                          size="xs"
                          placeholder="Link name"
                          searchable
                          data={
                            nameOptionsBySource[line.linkType as string] ?? []
                          }
                          value={line.linkName || null}
                          onChange={(value) =>
                            updateLine(line.key, { linkName: value ?? '' })
                          }
                          classNames={{ input: 'text-xs' }}
                        />
                      ) : line.linkType ? (
                        <Text size="xs" fw={300}>
                          {sourceLabel(line.linkType)}
                        </Text>
                      ) : (
                        <Select
                          size="xs"
                          placeholder="Source"
                          searchable
                          data={sourceOptions}
                          value={(line.linkType as string) ?? null}
                          onChange={(value) =>
                            updateLine(line.key, {
                              linkType: (value as LinkType) ?? null,
                            })
                          }
                          classNames={{ input: 'text-xs' }}
                        />
                      )}
                    </div>

                    <NumberInput
                      size="xs"
                      w={110}
                      min={0}
                      decimalScale={2}
                      prefix="$"
                      hideControls
                      thousandSeparator=","
                      value={line.amount}
                      onChange={(value) =>
                        updateLine(line.key, { amount: Number(value) || 0 })
                      }
                      classNames={{ input: 'text-xs text-right' }}
                    />

                    {/* The split slot is reserved on every row so the amount
                        inputs and remove buttons stay in one column. */}
                    {line.linkType && !isNameLine ? (
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="sm"
                        aria-label="Add link-name line"
                        onClick={() =>
                          addNameLine(line.key, line.linkType as LinkType)
                        }
                      >
                        <Plus size={14} />
                      </ActionIcon>
                    ) : (
                      <span className="w-[26px]" aria-hidden="true" />
                    )}
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      size="sm"
                      aria-label="Remove line"
                      onClick={() => removeLine(line.key)}
                    >
                      <Trash2 size={14} />
                    </ActionIcon>
                  </div>
                )
              })}
            </div>

            <button
              type="button"
              onClick={addSourceLine}
              className="w-full rounded-md border border-dashed border-doubleNickel-gray-300 py-1.5 text-xs text-doubleNickel-brand-500 hover:border-doubleNickel-brand-500 hover:bg-doubleNickel-brand-25"
            >
              + Add source
            </button>

            {onCopyPrevious && draftLines.length === 0 && (
              <DNButton
                variant="transparent"
                className="self-start"
                onClick={handleCopyPrevious}
                data-testid="copy-previous-button"
              >
                {copyPreviousLabel ?? 'Copy previous month'}
              </DNButton>
            )}

            <Text size="xs" fw={300} className="text-doubleNickel-gray-500">
              Use + on a source to split it by link name.
            </Text>

            {error && (
              <Text
                size="xs"
                fw={400}
                className="text-doubleNickel-error-500"
                data-testid="drawer-error"
              >
                {error}
              </Text>
            )}
          </>
        ) : loadFailed ? (
          // Never fall through to "No budget" here: a failed fetch and an
          // unbudgeted month both leave `budget` null, and telling the user the
          // month is empty invites them to re-enter it — which would delete the
          // lines that failed to load, since the upsert rewrites a whole month.
          <div className="flex flex-col items-start gap-2">
            <Text size="xs" fw={400} className="text-doubleNickel-error-500">
              Couldn&#39;t load {monthLabel}&#39;s budget.
            </Text>
            {onRetryLoad && (
              <DNButton
                variant="transparent"
                onClick={onRetryLoad}
                data-testid="retry-load-button"
              >
                Try again
              </DNButton>
            )}
          </div>
        ) : lines.length === 0 ? (
          <Text size="xs" fw={400} className="text-doubleNickel-gray-500">
            No budget for {monthLabel}.
          </Text>
        ) : (
          <div className="space-y-3" data-testid="budget-line-list">
            {grouped.map(([linkType, entry]) => (
              <div key={linkType} className="space-y-3">
                <DetailRow label={sourceLabel(linkType)}>
                  <Text size="xs" fw={400}>
                    {money.format(entry.total)}
                  </Text>
                </DetailRow>
                {entry.children.map((child) => (
                  <DetailRow
                    key={child.marketingBudgetLineId ?? child.linkName}
                    label={child.linkName}
                    indent
                  >
                    <Text
                      size="xs"
                      fw={400}
                      className="text-doubleNickel-gray-500"
                    >
                      {money.format(Number(child.amount))}
                    </Text>
                  </DetailRow>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export interface MarketingBudgetDrawerProps {
  opened: boolean
  /** First-of-month ISO string; null when the drawer is closed. */
  month: string | null
  onClose: () => void
  onSaved?: () => void
}

const MarketingBudgetDrawer = ({
  opened,
  month,
  onClose,
  onSaved,
}: MarketingBudgetDrawerProps) => {
  const { currentUser } = useAuth()
  const companyId = (currentUser as { companyId?: string } | null)?.companyId
  // The grant alone governs editing. `manage:marketing_costs` is a manage
  // permission, so whoever holds it can both see and set the budget — no
  // separate admin role required. The server applies the same rule.
  const canEdit = canAccessMarketingCosts(currentUser)

  const [activeMonth, setActiveMonth] = useState<string | null>(month)
  useEffect(() => setActiveMonth(month), [month])

  // Read inside async callbacks that started before the month changed.
  const activeMonthRef = useRef(activeMonth)
  useEffect(() => {
    activeMonthRef.current = activeMonth
  }, [activeMonth])

  // Bumped on every successful save so the view can drop back to read-only.
  // The month is carried alongside so a slow save can't close an editor the
  // user has since opened on a different month.
  const [savedAt, setSavedAt] = useState(0)
  const [savedMonth, setSavedMonth] = useState<string | null>(null)

  const { options, loadData } = useCompanyLeadSourceOptions()
  // Fire on the open TRANSITION, which is what the hook documents itself for.
  // Gating on `isLoaded` instead would permanently strand the Source dropdown:
  // the hook sets isLoaded on failure too (so menus stop saying "Loading…"),
  // and its own loadData already no-ops after a success and retries after an
  // error — so the only thing an isLoaded gate can do here is block the retry.
  const wasOpened = useRef(false)
  useEffect(() => {
    if (opened && !wasOpened.current) loadData()
    wasOpened.current = opened
  }, [opened, loadData])

  const {
    data,
    loading,
    error: loadError,
    refetch,
  } = useQuery(GET_MARKETING_BUDGET, {
    variables: { companyId, month: activeMonth },
    skip: !opened || !companyId || !activeMonth,
  })

  const [fetchPreviousMonth] = useLazyQuery(GET_MARKETING_BUDGET, {
    fetchPolicy: 'network-only',
  })

  const [upsertBudget, { loading: saving }] = useMutation(
    UPSERT_MARKETING_BUDGET,
    {
      onCompleted: (_result, clientOptions) => {
        const written = (
          clientOptions?.variables as { input?: { month?: string } } | undefined
        )?.input?.month
        toast('Budget saved', 'success')
        setSavedMonth(written ?? null)
        setSavedAt((n) => n + 1)
        // Refetch the month that was WRITTEN, not whichever one is on screen
        // now. `refetch()` reuses the hook's current variables, so stepping
        // months while the save is in flight re-pulls the new month and leaves
        // the saved month cached at its pre-save figure — the drawer then shows
        // a stale total for a budget it just wrote.
        if (written) {
          refetch({ companyId, month: written })
        } else {
          refetch()
        }
        onSaved?.()
      },
      onError: (mutationError) =>
        toast(mutationError.message ?? 'Failed to save budget', 'error'),
      // The per-source table reads the same budgets, so it has to re-pull too.
      refetchQueries: [GET_PIPELINE_SOURCE_BREAKDOWN],
    }
  )

  const budget = (data?.marketingBudget ?? null) as BudgetData | null

  const sourceOptions = useMemo(
    () =>
      options.map((option) => ({
        value: option.value,
        label: LinkTypeDisplay[option.value] ?? option.value,
      })),
    [options]
  )

  const nameOptionsBySource = useMemo(() => {
    const map: Record<string, string[]> = {}
    for (const option of options) {
      map[option.value] = (option.options ?? [])
        // The "(No name)" bucket can't carry a budget line — a budget line
        // with no link name means the whole source, not the unnamed links.
        .filter((child) => child.value !== LEAD_SOURCE_NO_NAME)
        .map((child) => child.value)
    }
    return map
  }, [options])

  // Returns the previous month's lines for the editor to adopt as unsaved
  // edits; null when there's nothing to copy.
  const handleCopyPrevious = useCallback(async (): Promise<
    BudgetLineInput[] | null
  > => {
    if (!companyId || !activeMonth) return null
    const previousMonth = dayjs
      .utc(activeMonth)
      .subtract(1, 'month')
      .startOf('month')
      .toISOString()
    const requestedFor = activeMonth
    const result = await fetchPreviousMonth({
      variables: { companyId, month: previousMonth },
    })
    if (result.error) {
      // Reporting a fetch failure as "nothing to copy" is a false claim about
      // another month's data — the user re-keys a budget that already exists.
      toast('Couldn’t load last month’s budget — try again', 'error')
      return null
    }
    // The user may have stepped months while this was in flight. Adopting it
    // now would drop one month's money into another month's draft, and the
    // save writes whichever month the header shows.
    if (requestedFor !== activeMonthRef.current) return null
    const previous = result.data?.marketingBudget as BudgetData | null
    if (!previous?.lines?.length) {
      toast('No budget lines to copy from the previous month', 'info')
      return null
    }
    toast('Copied last month’s lines — review and save', 'success')
    // Whitelist rather than strip: these are new lines for a different month,
    // and any leftover server field (id, __typename) would fail the save.
    return previous.lines.map((line) => ({
      linkType: line.linkType ?? null,
      linkName: line.linkName ?? null,
      amount: Number(line.amount) || 0,
    }))
  }, [activeMonth, companyId, fetchPreviousMonth])

  if (!activeMonth) return null

  return (
    <DnDrawer
      opened={opened}
      onClose={onClose}
      stackId="marketing-budget-drawer"
    >
      <MarketingBudgetDrawerView
        month={activeMonth}
        budget={budget}
        loading={loading}
        canEdit={canEdit}
        saving={saving}
        onMonthChange={setActiveMonth}
        onSave={(input) => upsertBudget({ variables: { companyId, input } })}
        savedAt={savedAt}
        savedMonth={savedMonth}
        loadFailed={!!loadError}
        onRetryLoad={() => refetch()}
        onCopyPrevious={handleCopyPrevious}
        copyPreviousLabel={`Copy ${dayjs
          .utc(activeMonth)
          .subtract(1, 'month')
          .format('MMMM')}`}
        sourceOptions={sourceOptions}
        nameOptionsBySource={nameOptionsBySource}
      />
    </DnDrawer>
  )
}

export default MarketingBudgetDrawer
